/**
 * Parsers for AuditEntry description fields.
 *
 * The backend audit_collector.py produces structured description strings
 * for each entry type. These parsers extract structured data from those
 * strings so the UI can render KeyValuePairs instead of raw text.
 *
 * If a description does not match the expected format the parser returns
 * null and the caller falls back to displaying raw text.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClassificationDetails {
  entityTypes: string;
  securityDomains: string;
  reasoning: string;
  entityTypesList: string[];
  securityDomainsList: string[];
}

export interface RuleExecutionDetails {
  ruleName: string;
  outcome: 'PASS' | 'FAIL' | 'UNKNOWN';
  severity: string | null;
  evaluatorType: string | null;
  durationMs: number | null;
  error: string | null;
  confidence: number | null;
  reasoning: {
    prompt: string;
    response: string;
    model?: string;
  } | null;
}

export interface ExternalServiceDetails {
  ruleName: string | null;
  inputTokens: number;
  outputTokens: number;
  modelLatencyMs: number | null;
}

export interface ErrorDetails {
  errorCode: string | null;
  errorCategory: string | null;
  stackTrace: string | null;
  context: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse a Python-style list string (e.g., "['IAM', 'S3']") into a string array.
 * Falls back to splitting on commas if not valid Python list syntax.
 * Returns empty array if input is empty or cannot be parsed.
 */
export function parsePythonListString(value: string): string[] {
  if (!value || value === '[]' || value === 'None') return [];

  // Try to parse as Python list: ['item1', 'item2']
  const listMatch = value.match(/^\[(.+)\]$/s);
  if (listMatch) {
    const inner = listMatch[1];
    const items: string[] = [];
    const re = /['"]([^'"]*)['"]/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(inner)) !== null) {
      items.push(m[1].trim());
    }
    if (items.length > 0) return items;
  }

  // Fallback: split on comma
  return value.split(',').map(s => s.trim()).filter(Boolean);
}

// ---------------------------------------------------------------------------
// Parsers
// ---------------------------------------------------------------------------

/**
 * Parse an INPUT_COLLECTION description produced by
 * ``_build_classification_entry`` in audit_collector.py.
 *
 * Expected format:
 *   "Classification: <entity_types>; domains: <domains>; reasoning: <reasoning>"
 */
export function parseClassificationEntry(
  description: string | undefined,
): ClassificationDetails | null {
  if (!description) return null;

  const match = description.match(
    /^Classification:\s*(.+?);\s*domains:\s*(.+?);\s*reasoning:\s*(.+)$/s,
  );
  if (!match) return null;

  const entityTypes = match[1].trim();
  const securityDomains = match[2].trim();
  return {
    entityTypes,
    securityDomains,
    reasoning: match[3].trim(),
    entityTypesList: parsePythonListString(entityTypes),
    securityDomainsList: parsePythonListString(securityDomains),
  };
}

/**
 * Parse a RULE_EXECUTION description produced by
 * ``_build_rule_execution_entries`` in audit_collector.py.
 *
 * Expected format:
 *   "Rule '<name>': PASS|FAIL|UNKNOWN"
 *   "Rule '<name>': PASS (severity: HIGH)"
 */
export function parseRuleExecutionEntry(
  description: string | undefined,
): RuleExecutionDetails | null {
  if (!description) return null;

  // Try the text-based regex first (existing format)
  const match = description.match(
    /^Rule\s+'([^']+)':\s*(PASS|FAIL|UNKNOWN)(?:\s*\(severity:\s*(\w+)\))?$/,
  );
  if (match) {
    return {
      ruleName: match[1],
      outcome: match[2] as 'PASS' | 'FAIL' | 'UNKNOWN',
      severity: match[3] ?? null,
      evaluatorType: null,
      durationMs: null,
      error: null,
      confidence: null,
      reasoning: null,
    };
  }

  // Try JSON format
  try {
    const parsed = JSON.parse(description);
    if (typeof parsed === 'object' && parsed !== null && parsed.rule_name) {
      const outcome = (parsed.outcome ?? 'UNKNOWN').toUpperCase();
      const reasoning = parsed.reasoning && typeof parsed.reasoning === 'object'
        ? {
            prompt: parsed.reasoning.prompt ?? '',
            response: parsed.reasoning.response ?? '',
            model: parsed.reasoning.model ?? undefined,
          }
        : null;
      return {
        ruleName: parsed.rule_name,
        outcome: (['PASS', 'FAIL', 'UNKNOWN'].includes(outcome) ? outcome : 'UNKNOWN') as 'PASS' | 'FAIL' | 'UNKNOWN',
        severity: parsed.severity ?? null,
        evaluatorType: parsed.evaluator_type ?? parsed.evaluatorType ?? null,
        durationMs: parsed.duration_ms ?? parsed.durationMs ?? null,
        error: parsed.error ?? null,
        confidence: parsed.confidence ?? null,
        reasoning,
      };
    }
  } catch {
    // Not JSON, fall through
  }

  return null;
}

/**
 * Parse an EXTERNAL_SERVICE_CALL description produced by
 * ``_build_external_service_entries`` in audit_collector.py.
 *
 * Expected format:
 *   "LLM call for <rule>: inputTokens=123, outputTokens=456"
 *   "LLM call: inputTokens=123, outputTokens=456"
 */
export function parseExternalServiceEntry(
  description: string | undefined,
): ExternalServiceDetails | null {
  if (!description) return null;

  // Try text-based regex (existing format), optionally with modelLatency
  const match = description.match(
    /^LLM call(?:\s+for\s+(.+?))?:\s*inputTokens=(\d+),\s*outputTokens=(\d+)(?:,\s*modelLatencyMs=(\d+))?$/,
  );
  if (match) {
    return {
      ruleName: match[1] ?? null,
      inputTokens: parseInt(match[2], 10),
      outputTokens: parseInt(match[3], 10),
      modelLatencyMs: match[4] ? parseInt(match[4], 10) : null,
    };
  }

  // Try JSON format
  try {
    const parsed = JSON.parse(description);
    if (typeof parsed === 'object' && parsed !== null && (parsed.input_tokens != null || parsed.inputTokens != null)) {
      return {
        ruleName: parsed.rule_name ?? parsed.ruleName ?? null,
        inputTokens: parsed.input_tokens ?? parsed.inputTokens ?? 0,
        outputTokens: parsed.output_tokens ?? parsed.outputTokens ?? 0,
        modelLatencyMs: parsed.model_latency_ms ?? parsed.modelLatencyMs ?? null,
      };
    }
  } catch {
    // Not JSON, fall through
  }

  return null;
}

/**
 * Parse an ERROR_OCCURRED description.
 *
 * The backend may produce a JSON description with error_code,
 * error_category, stack_trace, context fields. Falls back to null
 * if the description is not parseable JSON.
 */
export function parseErrorDescription(
  description: string | undefined,
): ErrorDetails | null {
  if (!description) return null;

  try {
    const parsed = JSON.parse(description);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return {
      errorCode: parsed.error_code ?? parsed.errorCode ?? null,
      errorCategory: parsed.error_category ?? parsed.errorCategory ?? null,
      stackTrace: parsed.stack_trace ?? parsed.stackTrace ?? null,
      context: parsed.context ?? null,
    };
  } catch {
    // Not JSON -- try simple "key: value" line format
    const errorCode = description.match(/error[_\s]?code:\s*(.+)/i)?.[1]?.trim() ?? null;
    const errorCategory = description.match(/error[_\s]?category:\s*(.+)/i)?.[1]?.trim() ?? null;
    const stackTrace = description.match(/stack[_\s]?trace:\s*(.+)/is)?.[1]?.trim() ?? null;
    const context = description.match(/context:\s*(.+)/i)?.[1]?.trim() ?? null;
    if (errorCode || errorCategory || stackTrace || context) {
      return { errorCode, errorCategory, stackTrace, context };
    }
    return null;
  }
}
